import React, { useState, useEffect, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import Tabs, { TabPane } from "rc-tabs";
import TabContent from "rc-tabs/lib/SwipeableTabContent";
import ScrollableInkTabBar from "rc-tabs/lib/ScrollableInkTabBar";
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../Loaders/DnaLoader";
import { Tooltip } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import ConfigurationService from "../../pages/Configuration/configuration.service.js";
import { getDisplayFileName, uploadFile, getMerchantLogoUrl } from "../../utils/fileUtils";
import { SensitivePageBanner, SensitiveText, useSensitiveAction, useSensitiveInfo } from "../../context/SensitiveInfoContext";
const API_BASE_URL = process.env.REACT_APP_APIURL;
const COMMISSION_PLACEHOLDER = "If no commission rate is set, default will be used";
const FILE_BASE_URL = process.env.REACT_APP_FILE_URL || "http://localhost:3012";

const formatCurrency = (value) => {
  const amount = Number(value) || 0;
  return `RM ${amount.toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatCard = ({ icon, label, value, subValue, color = "#00BFA6" }) => (
  <div className="col-md-3 col-sm-6 mb-3">
    <div className="card h-100 border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <p className="text-muted small mb-1 fw-bold">{label}</p>
            <h4 className="mb-0 fw-bold text-dark">{value}</h4>
            {subValue && (
              <p className="text-muted small mb-0 mt-1">{subValue}</p>
            )}
          </div>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: 42,
              height: 42,
              backgroundColor: `${color}20`,
              color,
            }}
          >
            <i className={`${icon} fs-5`}></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DetailRow = ({ label, value, highlight }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted small">{label}</span>
    <span className={`fw-bold ${highlight || "text-dark"}`}>{value}</span>
  </div>
);

const StatsPanel = ({ loading, stats, t, children }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">{t("Loading...")}</span>
        </div>
        <p className="text-muted mt-2 mb-0">{t("Loading pharmacy statistics...")}</p>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="alert alert-light border mb-0">
        {t("Unable to load pharmacy statistics.")}
      </div>
    );
  }
  return children;
};

const MerchantView = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isProtected, isUnlocked } = useSensitiveInfo();
  const runSensitive = useSensitiveAction();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [merchantDocs, setMerchantDocs] = useState([]);
  const fileInputRef = useRef(null); // Create a ref for the file input element
  // Edit mode states for different sections
  const [editingBasic, setEditingBasic] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);

  // Form data states
  const [basicInfo, setBasicInfo] = useState({});
  const [addressInfo, setAddressInfo] = useState({});
  const [financialInfo, setFinancialInfo] = useState({});
  const [settingsInfo, setSettingsInfo] = useState({});

  // Partner pharmacy states
  const [editingPartner, setEditingPartner] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState({
    isPartnerPharmacy: false,
    partnerVisibleOnPatientApp: false,
  });
  const [savingPartner, setSavingPartner] = useState(false);

  // File upload states
  const [documentFile, setDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState("businessRegistration");
  const [documentDescription, setDocumentDescription] = useState("");
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [defaultShare, setDefaultShare] = useState(null);
  const [merchantStats, setMerchantStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchDefaultShare = async () => {
    try {
      const sessionID = localStorage.getItem("session-id");
      const response = await ConfigurationService.getConfiguration(sessionID);
      if (response.data?.data?.[0]?.sara_share != null) {
        setDefaultShare(response.data.data[0].sara_share);
      }
    } catch (error) {
      console.error("Failed to load default commission rate:", error);
    }
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchDefaultShare();
    fetchMerchantData();
    fetchMerchantStats();
  }, [id]);

  useEffect(() => {
    if (merchant) {
      // Initialize form data from merchant
      setBasicInfo({
        merchantName: merchant.merchantName || "",
        merchantType: merchant.merchantType || "",
        companyName: merchant.companyName || "",
        registrationNumber: merchant.registrationNumber || "",
        businessCategory: merchant.businessCategory || "",
        contactPerson: merchant.contactPerson || "",
        contactEmail: merchant.contactEmail || "",
        contactPhone: merchant.contactPhone || "",
        alternativePhone: merchant.alternativePhone || "",
        loginEmail: merchant.loginEmail || "",
      });

      setAddressInfo({
        line1: merchant.address?.line1 || "",
        line2: merchant.address?.line2 || "",
        city: merchant.address?.city || "",
        state: merchant.address?.state || "",
        postcode: merchant.address?.postcode || "",
        country: merchant.address?.country || "",
        googleMapLocation: merchant.address?.googleMapLocation || "",
      });

      setFinancialInfo({
        bankName: merchant.bankInfo?.bankName || "",
        accountNumber: merchant.bankInfo?.accountNumber || "",
        accountHolderName: merchant.bankInfo?.accountHolderName || "",
        paymentTerms: merchant.bankInfo?.paymentTerms || "",
        commissionRate: merchant.hasCustomCommissionRate ? (merchant.commissionRate ?? "") : "",
      });

      setSettingsInfo({
        storeDescription: merchant.storeDescription || "",
        openingHours: merchant.openingHours || {
          monday: { open: "", close: "" },
          tuesday: { open: "", close: "" },
          wednesday: { open: "", close: "" },
          thursday: { open: "", close: "" },
          friday: { open: "", close: "" },
          saturday: { open: "", close: "" },
          sunday: { open: "", close: "" },
        },
      });

      setPartnerInfo({
        isPartnerPharmacy: !!merchant.isPartnerPharmacy,
        partnerVisibleOnPatientApp: !!merchant.partnerVisibleOnPatientApp,
      });
    }
  }, [merchant]);

  const fetchMerchantStats = async () => {
    try {
      setStatsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/MerchantModule/stats/${id}`);
      if (response.data.success) {
        setMerchantStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching merchant stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchMerchantData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/MerchantModule/${id}`);

      if (response.data.success) {
        setMerchant(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching merchant data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load merchant data",
        text:
          error.response?.data?.message ||
          "An error occurred while fetching merchant data",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const requestData = {
        functionName: 'GetBanks',
        postData: {
          offset: 0,
          limit: 100,
          sort: { datetime_added: -1 }
        }
      };

      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('session-id')
      };

      const response = await axios.post(`${API_BASE_URL}/GetBanks`, requestData, { headers });
      
      if (response.data.status) {
        setBanks(response.data.data || []);
      } else {
        console.error("Error fetching banks:", response.data.message);
        setBanks([]);
      }
    } catch (err) {
      console.error("Error fetching banks:", err);
      setBanks([]); // Set empty array on error
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleDelete = async () => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00BFA6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const response = await axios.delete(
          `${API_BASE_URL}/MerchantModule/delete/${id}`
        );
        if (response.data.success) {
          Swal.fire("Deleted!", "Merchant has been deleted.", "success");
          navigate("/merchants");
        }
      }
    } catch (error) {
      console.error("Error deleting merchant:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to delete merchant",
        text:
          error.response?.data?.message ||
          "An error occurred while deleting merchant",
      });
    }
  };

  const handleCredentialResponse = (response) => {
    if (response.data.message && response.data.message.includes("not yet verified")) {
      Swal.fire({
        icon: "warning",
        title: t("Email Not Verified"),
        text: response.data.message,
        confirmButtonColor: "#00BFA6",
      });
    } else {
      Swal.fire({
        icon: "success",
        title: t("Credentials Sent"),
        text: response.data.message || t("Login credentials have been sent to the merchant email"),
        confirmButtonColor: "#00BFA6",
      });
    }
  };

  const handleSendCredentials = async () => {
    const result = await Swal.fire({
      title: t("Send Credentials?"),
      text: t("Current login credentials will be sent to the merchant's email. The password will not be changed."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00BFA6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, send credentials"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setSendingCredentials(true);
      const response = await axios.post(
        `${API_BASE_URL}/MerchantModule/send-credentials/${id}`
      );

      if (response.data.success) {
        handleCredentialResponse(response);
      }
    } catch (error) {
      console.error("Error sending credentials:", error);
      Swal.fire({
        icon: "error",
        title: t("Failed to Send Credentials"),
        text:
          error.response?.data?.message ||
          t("An error occurred while sending login credentials"),
      });
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleResetPassword = async () => {
    const result = await Swal.fire({
      title: t("Reset Password?"),
      text: t("A new password will be generated and sent to the merchant's email. The merchant will be logged out from all devices."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00BFA6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, reset password"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setResettingPassword(true);
      const response = await axios.post(
        `${API_BASE_URL}/MerchantModule/reset-credentials/${id}`
      );

      if (response.data.success) {
        handleCredentialResponse(response);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      Swal.fire({
        icon: "error",
        title: t("Failed to Reset Password"),
        text:
          error.response?.data?.message ||
          t("An error occurred while resetting the password"),
      });
    } finally {
      setResettingPassword(false);
    }
  };

  // Handlers for form updates
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleFinancialChange = (e) => {
    const { name, value } = e.target;
    setFinancialInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setSettingsInfo((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value,
        },
      },
    }));
  };

  // Form submission handlers
  const handleSaveBasicInfo = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-basic/${id}`,
        basicInfo
      );

      if (response.data.success) {
        setMerchant((prev) => ({ ...prev, ...basicInfo }));
        setEditingBasic(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Basic information updated successfully",
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating basic info:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message || "Failed to update basic information",
      });
    }
  };

  const handleSaveAddress = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-address/${id}`,
        addressInfo
      );

      if (response.data.success) {
        setMerchant((prev) => ({ ...prev, address: addressInfo }));
        setEditingAddress(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Address information updated successfully",
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating address:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          "Failed to update address information",
      });
    }
  };

  const handleSaveFinancial = async () => {
    try {
      // Split into bank info and commission rate
      const { commissionRate, ...bankDetails } = financialInfo;

      // First update bank info
      const bankResponse = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-bank/${id}`,
        bankDetails
      );

      // Then update commission rate (part of settings)
      const settingsResponse = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-settings/${id}`,
        {
          commissionRate: commissionRate === '' ? null : commissionRate,
          storeDescription: merchant.storeDescription,
          openingHours: merchant.openingHours,
        }
      );

      if (bankResponse.data.success && settingsResponse.data.success) {
        setMerchant((prev) => ({
          ...prev,
          bankInfo: bankDetails,
          commissionRate,
        }));
        setEditingFinancial(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Financial information updated successfully",
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating financial info:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          "Failed to update financial information",
      });
    }
  };

  const handleSaveSettings = async () => {
    try {
      const settingsData = {
        storeDescription: settingsInfo.storeDescription,
        openingHours: settingsInfo.openingHours,
        commissionRate: merchant.commissionRate,
      };

      const response = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-settings/${id}`,
        settingsData
      );

      if (response.data.success) {
        setMerchant((prev) => ({
          ...prev,
          storeDescription: settingsInfo.storeDescription,
          openingHours: settingsInfo.openingHours,
        }));
        setEditingSettings(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Store settings updated successfully",
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message || "Failed to update store settings",
      });
    }
  };

  const handlePartnerChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setPartnerInfo((prev) => {
      const updated = { ...prev, [name]: nextValue };
      if (name === "isPartnerPharmacy" && !nextValue) {
        updated.partnerVisibleOnPatientApp = false;
      }
      return updated;
    });
  };

  const handleSavePartner = async () => {
    try {
      setSavingPartner(true);
      const response = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-partner/${id}`,
        partnerInfo
      );

      if (response.data.success) {
        setMerchant(response.data.data);
        setEditingPartner(false);
        Swal.fire({
          icon: "success",
          title: t("Success"),
          text: t("Partner pharmacy settings updated successfully"),
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating partner pharmacy settings:", error);
      Swal.fire({
        icon: "error",
        title: t("Update Failed"),
        text:
          error.response?.data?.message ||
          t("Failed to update partner pharmacy settings"),
      });
    } finally {
      setSavingPartner(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();

    if (!documentFile) {
      Swal.fire({
        icon: "warning",
        title: "No file selected",
        text: "Please select a file to upload",
      });
      return;
    }

    try {
      setUploadingDocument(true);

      const formData = new FormData();
      formData.append("document", documentFile);

      if (documentType === "other" && documentDescription) {
        formData.append("description", documentDescription);
      }

      const response = await axios.post(
        `${API_BASE_URL}/MerchantModule/upload-document/${id}/${documentType}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        // Update merchant state with new document
        setMerchant(response.data.data);

        // Reset form
        setDocumentFile(null);
        setDocumentDescription("");
        setDocumentType("businessRegistration");
        fileInputRef.current.value = ""; // Use the ref to reset the file input

        Swal.fire({
          icon: "success",
          title: "Document Uploaded",
          text: "Document has been uploaded successfully",
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: error.response?.data?.message || "Failed to upload document",
      });
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleStatusUpdate = async (e) => {
    const newStatus = e.target.value;

    if (newStatus === merchant.status) return;

    const result = await Swal.fire({
      title: t("Change Status?"),
      text: t('Are you sure you want to change the merchant status to "{{status}}"?', {
        status: newStatus,
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00BFA6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, change status"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setUpdatingStatus(true);
      const response = await axios.put(
        `${API_BASE_URL}/MerchantModule/update-status/${id}`,
        {
          status: newStatus,
        }
      );

      if (response.data.success) {
        setMerchant((prev) => ({ ...prev, status: newStatus }));
        Swal.fire({
          icon: "success",
          title: t("Status Updated"),
          text: t("Merchant status has been updated successfully."),
          confirmButtonColor: "#00BFA6",
        });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire({
        icon: "error",
        title: t("Failed to Update Status"),
        text:
          error.response?.data?.message ||
          t("An error occurred while updating status."),
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Add fetchMerchantDocs function
  const fetchMerchantDocs = async () => {
    try {
      const requestData = {
        functionName: 'GetMerchantDocs',
        postData: {
          offset: 0,
          limit: 100,
          sort: { datetime_added: -1 }
        }
      };

      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('session-id')
      };

      const response = await axios.post(`${API_BASE_URL}/GetMerchantDocs`, requestData, { headers });
      
      if (response.data.status) {
        setMerchantDocs(response.data.data || []);
      } else {
        console.error("Error fetching merchant docs:", response.data.message);
        setMerchantDocs([]);
      }
    } catch (error) {
      console.error("Error fetching merchant docs:", error);
      setMerchantDocs([]);
    }
  };

  useEffect(() => {
    fetchMerchantDocs();
  }, []);

  // Fix payment terms if they are invalid
  useEffect(() => {
    if (merchant && merchant.bankInfo && merchant.bankInfo.paymentTerms) {
      const invalidTerms = ["Net 7", "Net 15", "Net 30", "Net 45", "Net 60"];
      if (invalidTerms.includes(merchant.bankInfo.paymentTerms)) {
        // Auto-fix the payment terms
        const paymentTermsMapping = {
          "Net 7": "Weekly",
          "Net 15": "Bi-Weekly",
          "Net 30": "Monthly",
          "Net 45": "Monthly",
          "Net 60": "Monthly"
        };
        
        const newPaymentTerms = paymentTermsMapping[merchant.bankInfo.paymentTerms] || "Monthly";
        
        // Update the merchant's payment terms
        const updatePaymentTerms = async () => {
          try {
            const response = await axios.put(
              `${API_BASE_URL}/MerchantModule/update-bank/${id}`,
              {
                ...merchant.bankInfo,
                paymentTerms: newPaymentTerms
              }
            );
            
            if (response.data.success) {
              setMerchant(prev => ({
                ...prev,
                bankInfo: {
                  ...prev.bankInfo,
                  paymentTerms: newPaymentTerms
                }
              }));
              console.log(`Payment terms updated from ${merchant.bankInfo.paymentTerms} to ${newPaymentTerms}`);
            }
          } catch (error) {
            console.error("Error updating payment terms:", error);
          }
        };
        
        updatePaymentTerms();
      }
    }
  }, [merchant]);

  if (loading) {
    return (
      <Fragment>
        <div className="app-main__outer">
          <div className="app-main__inner">
            <div className="bg-light-gray bg-animation">
              <div className="d-flex justify-content-center align-items-center">
                <Col md="12" className="mx-auto app-login-box">
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <div style={{ height: '400px' }}></div>
                  </LoadingOverlay>
                </Col>
      </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  if (!merchant) {
    return (
      <Fragment>
        <div className="app-main__outer">
          <div className="app-main__inner">
            <div className="bg-light-gray bg-animation">
              <div className="d-flex justify-content-center align-items-center">
                <Col md="12" className="mx-auto app-login-box">
                  <Card className="main-card mb-3">
                    <CardBody>
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <p className="text-muted fs-4">{t("Merchant not found")}</p>
        </div>
                    </CardBody>
                  </Card>
                </Col>
      </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
    <div className="app-main__outer">
      <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-user icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {t("Merchant Details")}
                        <div className="page-title-subheading">
                          {t("View and manage merchant information")}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button
                        type="button"
                        className="btn btn-custom-color btn-hover-shine btn"
                        onClick={() => navigate("/merchants")}
                      >
                        <i className="pe-7s-back-2"></i> {t("Back to Merchants")}
                      </button>
                    </div>
                  </div>
                </div>

                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <SensitivePageBanner />
          {/* Header with Logo and Actions */}
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                {getMerchantLogoUrl(merchant?.logo) ? (
                                  <Tooltip placement="top" title={`${t("Merchant Logo")}`} arrow>
                  <img
                    src={getMerchantLogoUrl(merchant.logo)}
                    alt={merchant.merchantName}
                                      className="rounded-circle"
                                      style={{
                                        width: '64px',
                                        height: '64px',
                                        objectFit: 'cover',
                                        border: '3px solid #e0e0e0',
                                        cursor: 'pointer'
                                      }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <div 
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                    style={{
                                      width: '64px',
                                      height: '64px',
                                      backgroundColor: '#6c757d',
                                      fontSize: '24px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                    {merchant.merchantName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                                <h2 className="mb-1 fw-bold text-dark">
                  {merchant.merchantName}
                                </h2>
                                <div className="d-flex align-items-center">
                  <span
                                    className={`badge me-2 ${
                      merchant.status === "Active"
                                        ? "bg-success"
                        : merchant.status === "Inactive"
                                        ? "bg-danger"
                                        : "bg-warning"
                    }`}
                                    style={{ fontSize: '0.75rem' }}
                  >
                    {merchant.status}
                  </span>
                                  {merchant.isPartnerPharmacy && (
                                    <span className="badge bg-info me-2" style={{ fontSize: '0.75rem' }}>
                                      {t("Partner Pharmacy")}
                                    </span>
                                  )}
                                  <span className="text-muted small">
                    {merchant.merchantType}
                  </span>
                </div>
              </div>
            </div>
                            <div className="d-flex gap-2">
                              <div className="me-2 d-flex align-items-center gap-2">
                <select
                  value={merchant.status}
                  onChange={handleStatusUpdate}
                                  className="form-select form-select-sm"
                                  style={{ minWidth: '140px' }}
                                  disabled={updatingStatus || sendingCredentials || resettingPassword}
                                >
                                  <option value="Under Approval">{t("Under Approval")}</option>
                                  <option value="Active">{t("Active")}</option>
                                  <option value="Inactive">{t("Inactive")}</option>
                </select>
                {updatingStatus && (
                  <span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
                )}
              </div>

                              <Tooltip placement="top" title={t("Send Login Credentials")} arrow>
              <button
                onClick={handleSendCredentials}
                disabled={sendingCredentials || resettingPassword || updatingStatus}
                                  className="btn btn-success btn-sm btn-hover-shine"
              >
                                  {sendingCredentials ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      {t("Sending...")}
                                    </>
                                  ) : (
                                    <>
                                      <i className="pe-7s-mail"></i> {t("Send Credentials")}
                                    </>
                                  )}
              </button>
                              </Tooltip>
                              <Tooltip placement="top" title={t("Reset Password")} arrow>
              <button
                onClick={handleResetPassword}
                disabled={sendingCredentials || resettingPassword || updatingStatus}
                                  className="btn btn-warning btn-sm btn-hover-shine"
              >
                                  {resettingPassword ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      {t("Resetting...")}
                                    </>
                                  ) : (
                                    <>
                                      <i className="pe-7s-key"></i> {t("Reset Password")}
                                    </>
                                  )}
              </button>
                              </Tooltip>
                              <Tooltip placement="top" title={t("Delete Merchant")} arrow>
              <button
                onClick={handleDelete}
                                  className="btn btn-danger btn-sm btn-hover-shine"
              >
                                  <i className="pe-7s-trash"></i> {t("Delete")}
              </button>
                              </Tooltip>
            </div>
          </div>

                          <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            renderTabBar={() => <ScrollableInkTabBar />}
                            renderTabContent={() => <TabContent animated={false} />}
                          >
                            {/* Tab 1: Overview */}
                            <TabPane tab={t("Overview")} key="overview">
                              <div className="d-flex justify-content-end mb-3">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={fetchMerchantStats}
                                  disabled={statsLoading}
                                >
                                  <i className="pe-7s-refresh"></i> {t("Refresh")}
                                </button>
                              </div>
                              <StatsPanel loading={statsLoading} stats={merchantStats} t={t}>
                                <Row>
                                  <StatCard
                                    icon="pe-7s-users"
                                    label={t("Total Clients (Patients)")}
                                    value={merchantStats?.clients?.total_unique_patients || 0}
                                    subValue={`${t("Device")}: ${merchantStats?.clients?.device_order_patients || 0} · ${t("Prescribed")}: ${merchantStats?.clients?.prescribed_order_patients || 0}`}
                                    color="#2196F3"
                                  />
                                  <StatCard
                                    icon="pe-7s-cart"
                                    label={t("Total Orders")}
                                    value={merchantStats?.orders?.total || 0}
                                    subValue={`${t("Cancelled")}: ${merchantStats?.orders?.total_cancelled || 0}`}
                                    color="#FF9800"
                                  />
                                  <StatCard
                                    icon="pe-7s-cash"
                                    label={t("Total Revenue (Paid)")}
                                    value={formatCurrency(merchantStats?.revenue?.total)}
                                    subValue={`${t("Ledger")}: ${formatCurrency(merchantStats?.revenue?.ledger_total)}`}
                                    color="#4CAF50"
                                  />
                                  <StatCard
                                    icon="pe-7s-box2"
                                    label={t("Inventory Items")}
                                    value={merchantStats?.inventory?.total_inventory || 0}
                                    subValue={`${t("Low stock")}: ${merchantStats?.inventory?.low_on_stock || 0} · ${t("Out of stock")}: ${merchantStats?.inventory?.out_of_stock || 0}`}
                                    color="#9C27B0"
                                  />
                                </Row>
                                <Row className="mt-2">
                                  <Col md="4" className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-id me-2 text-primary"></i>
                                          {t("Account Summary")}
                                        </h6>
                                        <DetailRow label={t("Member Since")} value={formatDate(merchantStats?.account?.member_since)} />
                                        <DetailRow
                                          label={t("Email Verified")}
                                          value={merchantStats?.account?.is_email_verified ? t("Yes") : t("No")}
                                          highlight={merchantStats?.account?.is_email_verified ? "text-success" : "text-warning"}
                                        />
                                        <DetailRow label={t("Account Status")} value={merchantStats?.account?.status || merchant.status} />
                                        <DetailRow label={t("Merchant Type")} value={merchantStats?.account?.merchant_type || merchant.merchantType} />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="4" className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-star me-2 text-warning"></i>
                                          {t("Reviews")}
                                        </h6>
                                        <DetailRow label={t("Total Reviews")} value={merchantStats?.reviews?.total || 0} />
                                        <DetailRow
                                          label={t("Average Rating")}
                                          value={
                                            merchantStats?.reviews?.total > 0
                                              ? `${merchantStats?.reviews?.average_rating} / 5`
                                              : t("No reviews yet")
                                          }
                                          highlight="text-warning"
                                        />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="4" className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-wallet me-2 text-success"></i>
                                          {t("Quick Financials")}
                                        </h6>
                                        <DetailRow
                                          label={t("Total Earned")}
                                          value={formatCurrency(merchantStats?.ledger?.total_overall_amount)}
                                          highlight="text-success"
                                        />
                                        <DetailRow
                                          label={t("Total Withdrawn")}
                                          value={formatCurrency(merchantStats?.withdrawals?.total_withdrawn)}
                                        />
                                        <DetailRow
                                          label={t("Pending Withdrawals")}
                                          value={merchantStats?.withdrawals?.pending || 0}
                                          highlight="text-warning"
                                        />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </StatsPanel>
                            </TabPane>

                            {/* Tab 2: Partner Pharmacy */}
                            <TabPane
                              tab={
                                <span>
                                  <i className="pe-7s-handshake me-1"></i>
                                  {t("Partner")}
                                </span>
                              }
                              key="partner"
                            >
                              <Card className="border-0 shadow-sm">
                                <CardBody>
                                  <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h5 className="card-title mb-0 fw-bold">
                                      <i className="pe-7s-handshake me-2 text-info"></i>
                                      {t("Partner Pharmacy")}
                                    </h5>
                                    <Tooltip placement="top" title={editingPartner ? t("Cancel Edit") : t("Edit")} arrow>
                                      <button
                                        type="button"
                                        onClick={() => setEditingPartner(!editingPartner)}
                                        className={`btn btn-sm ${editingPartner ? "btn-secondary" : "btn-custom-color"} btn-hover-shine`}
                                      >
                                        <i className={`pe-7s-${editingPartner ? "close" : "edit"}`}></i>{" "}
                                        {editingPartner ? t("Cancel") : t("Edit")}
                                      </button>
                                    </Tooltip>
                                  </div>

                                  {editingPartner ? (
                                    <>
                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <div className="form-check form-switch">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id="isPartnerPharmacy"
                                              name="isPartnerPharmacy"
                                              checked={partnerInfo.isPartnerPharmacy}
                                              onChange={handlePartnerChange}
                                            />
                                            <label className="form-check-label fw-bold" htmlFor="isPartnerPharmacy">
                                              {t("Mark as Partner Pharmacy")}
                                            </label>
                                          </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <div className="form-check form-switch">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id="partnerVisibleOnPatientApp"
                                              name="partnerVisibleOnPatientApp"
                                              checked={partnerInfo.partnerVisibleOnPatientApp}
                                              onChange={handlePartnerChange}
                                              disabled={!partnerInfo.isPartnerPharmacy}
                                            />
                                            <label className="form-check-label fw-bold" htmlFor="partnerVisibleOnPatientApp">
                                              {t("Visible on Patient App")}
                                            </label>
                                          </div>
                                        </Col>
                                      </Row>
                                      <p className="text-muted small mb-3">
                                        {t("Pharmacy name and store logo will be shown on the Patient App.")}
                                      </p>
                                      <div className="d-flex justify-content-end">
                                        <button
                                          type="button"
                                          className="btn btn-success btn-hover-shine btn-sm"
                                          onClick={handleSavePartner}
                                          disabled={savingPartner}
                                        >
                                          {savingPartner ? t("Saving...") : t("Save")}
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <Row>
                                      <Col md="6" className="mb-3">
                                        <DetailRow
                                          label={t("Partner Status")}
                                          value={merchant.isPartnerPharmacy ? t("Partner") : t("Standard")}
                                          highlight={merchant.isPartnerPharmacy ? "text-info" : "text-muted"}
                                        />
                                      </Col>
                                      <Col md="6" className="mb-3">
                                        <DetailRow
                                          label={t("Patient App Visibility")}
                                          value={
                                            merchant.isPartnerPharmacy
                                              ? merchant.partnerVisibleOnPatientApp
                                                ? t("Visible")
                                                : t("Hidden")
                                              : t("N/A")
                                          }
                                          highlight={merchant.partnerVisibleOnPatientApp ? "text-success" : "text-muted"}
                                        />
                                      </Col>
                                      <Col md="6" className="mb-3">
                                        <DetailRow
                                          label={t("Pharmacy Name")}
                                          value={merchant.merchantName || "-"}
                                        />
                                      </Col>
                                      <Col md="6" className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                          <span className="text-muted small">{t("Store Logo")}</span>
                                          {getMerchantLogoUrl(merchant.logo) ? (
                                            <img
                                              src={getMerchantLogoUrl(merchant.logo)}
                                              alt={merchant.merchantName}
                                              className="rounded-circle"
                                              style={{ width: 36, height: 36, objectFit: "cover" }}
                                            />
                                          ) : (
                                            <span className="fw-bold text-muted">{t("Not set")}</span>
                                          )}
                                        </div>
                                      </Col>
                                    </Row>
                                  )}
                                </CardBody>
                              </Card>
                            </TabPane>

                            {/* Tab 3: Orders & Clients */}
                            <TabPane tab={t("Orders & Clients")} key="orders">
                              <StatsPanel loading={statsLoading} stats={merchantStats} t={t}>
                                <Row>
                                  <Col md="6" className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-users me-2 text-primary"></i>
                                          {t("Clients")}
                                        </h6>
                                        <DetailRow label={t("Total Clients (Patients)")} value={merchantStats?.clients?.total_unique_patients || 0} />
                                        <DetailRow label={t("Device Order Clients")} value={merchantStats?.clients?.device_order_patients || 0} />
                                        <DetailRow label={t("Prescribed Order Clients")} value={merchantStats?.clients?.prescribed_order_patients || 0} />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="6" className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-shuffle me-2 text-danger"></i>
                                          {t("Order Summary")}
                                        </h6>
                                        <DetailRow label={t("Total Orders")} value={merchantStats?.orders?.total || 0} />
                                        <DetailRow label={t("Total Cancelled")} value={merchantStats?.orders?.total_cancelled || 0} highlight="text-danger" />
                                        <DetailRow label={t("Total Revenue (Paid)")} value={formatCurrency(merchantStats?.revenue?.total)} highlight="text-success" />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="12" className="mb-4">
                                    <Card className="border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-shopbag me-2 text-warning"></i>
                                          {t("Orders Breakdown")}
                                        </h6>
                                        <Row>
                                          <Col md="6">
                                            <p className="text-muted small fw-bold mb-2">{t("Device / IoT Orders")}</p>
                                            <DetailRow label={t("Total")} value={merchantStats?.orders?.device_orders?.total || 0} />
                                            <DetailRow label={t("Paid")} value={merchantStats?.orders?.device_orders?.paid || 0} highlight="text-success" />
                                            <DetailRow label={t("Pending")} value={merchantStats?.orders?.device_orders?.pending || 0} highlight="text-warning" />
                                            <DetailRow label={t("Cancelled")} value={merchantStats?.orders?.device_orders?.cancelled || 0} highlight="text-danger" />
                                            <DetailRow label={t("Revenue")} value={formatCurrency(merchantStats?.orders?.device_orders?.revenue)} highlight="text-success" />
                                          </Col>
                                          <Col md="6">
                                            <p className="text-muted small fw-bold mb-2">{t("Prescribed Orders")}</p>
                                            <DetailRow label={t("Total Bids")} value={merchantStats?.orders?.prescribed_orders?.total_bids || 0} />
                                            <DetailRow label={t("Paid Items")} value={merchantStats?.orders?.prescribed_orders?.paid_items || 0} />
                                            <DetailRow label={t("Paid Orders")} value={merchantStats?.orders?.prescribed_orders?.paid_orders || 0} highlight="text-success" />
                                            <DetailRow label={t("Cancelled Orders")} value={merchantStats?.orders?.prescribed_orders?.cancelled_orders || 0} highlight="text-danger" />
                                            <DetailRow label={t("Revenue")} value={formatCurrency(merchantStats?.orders?.prescribed_orders?.revenue)} highlight="text-success" />
                                          </Col>
                                        </Row>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </StatsPanel>
                            </TabPane>

                            {/* Tab 3: Financial */}
                            <TabPane tab={t("Financial")} key="financial">
                              <StatsPanel loading={statsLoading} stats={merchantStats} t={t}>
                                <Row>
                                  <Col md="6" className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-wallet me-2 text-success"></i>
                                          {t("Ledger Summary")}
                                        </h6>
                                        <DetailRow label={t("Total Earned (Ledger)")} value={formatCurrency(merchantStats?.ledger?.total_overall_amount)} highlight="text-success" />
                                        <DetailRow label={t("Prescribed Medicines Earnings")} value={formatCurrency(merchantStats?.ledger?.prescribed_medicines_total_amount)} />
                                        <DetailRow label={t("IoT Device Earnings")} value={formatCurrency(merchantStats?.ledger?.iot_device_total_amount)} />
                                        <DetailRow label={t("Total Transactions")} value={merchantStats?.ledger?.total_transactions || 0} />
                                        <DetailRow label={t("Credit Transactions")} value={merchantStats?.ledger?.credit_count || 0} highlight="text-success" />
                                        <DetailRow label={t("Debit Transactions")} value={merchantStats?.ledger?.debit_count || 0} highlight="text-danger" />
                                        <DetailRow label={t("Pending Transactions")} value={merchantStats?.ledger?.pending_count || 0} highlight="text-warning" />
                                        <DetailRow label={t("Paid Transactions")} value={merchantStats?.ledger?.paid_count || 0} />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="6" className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-cash me-2 text-warning"></i>
                                          {t("Withdrawals")}
                                        </h6>
                                        <DetailRow label={t("Total Withdrawn")} value={formatCurrency(merchantStats?.withdrawals?.total_withdrawn)} />
                                        <DetailRow label={t("Total Requests")} value={merchantStats?.withdrawals?.total || 0} />
                                        <DetailRow label={t("Pending")} value={merchantStats?.withdrawals?.pending || 0} highlight="text-warning" />
                                        <DetailRow label={t("Approved")} value={merchantStats?.withdrawals?.approved || 0} highlight="text-success" />
                                        <DetailRow label={t("Rejected")} value={merchantStats?.withdrawals?.rejected || 0} highlight="text-danger" />
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col md="12" className="mb-4">
                                    <Card className="border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-clock me-2 text-primary"></i>
                                          {t("Recent Transactions")}
                                        </h6>
                                        {merchantStats?.recent_transactions?.length > 0 ? (
                                          merchantStats?.recent_transactions.map((txn) => (
                                            <div
                                              key={txn._id}
                                              className="p-2 mb-2 rounded bg-light border-start border-3"
                                              style={{
                                                borderColor:
                                                  txn.ladger_action === "+"
                                                    ? "#4CAF50"
                                                    : txn.ladger_action === "-"
                                                    ? "#f44336"
                                                    : "#FF9800",
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-start">
                                                <div className="me-2">
                                                  <div className="fw-bold small text-dark">
                                                    {txn.required_action || txn.type || t("Transaction")}
                                                  </div>
                                                  <div className="text-muted small">
                                                    {formatDate(txn.datetime_added)}
                                                    {txn.transaction_number ? ` · #${txn.transaction_number}` : ""}
                                                  </div>
                                                </div>
                                                <div className="text-end">
                                                  <div
                                                    className={`fw-bold small ${
                                                      txn.ladger_action === "+"
                                                        ? "text-success"
                                                        : txn.ladger_action === "-"
                                                        ? "text-danger"
                                                        : "text-dark"
                                                    }`}
                                                  >
                                                    {txn.ladger_action === "+" ? "+" : txn.ladger_action === "-" ? "-" : ""}
                                                    {formatCurrency(txn.pharmacy_actual_amount || txn.total_amount)}
                                                  </div>
                                                  <span
                                                    className={`badge ${
                                                      txn.status === "Paid"
                                                        ? "bg-success"
                                                        : txn.status === "Pending"
                                                        ? "bg-warning text-dark"
                                                        : "bg-secondary"
                                                    }`}
                                                    style={{ fontSize: "0.65rem" }}
                                                  >
                                                    {txn.status || "-"}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <p className="text-muted fst-italic mb-0">{t("No transactions found")}</p>
                                        )}
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </StatsPanel>
                              <Row>
                                <Col md="12" className="mb-4">
                                  <Card className="h-100">
                                    <CardBody>
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="card-title mb-0 fw-bold">
                                          <i className="pe-7s-credit me-2 text-primary"></i>
                                          {t("Bank & Payment Details")}
                                        </h5>
                                        <Tooltip placement="top" title={editingFinancial ? t("Cancel Edit") : t("Edit Financial Info")} arrow>
                                          <button
                                            onClick={() => setEditingFinancial(!editingFinancial)}
                                            className={`btn btn-sm ${editingFinancial ? 'btn-secondary' : 'btn-custom-color'} btn-hover-shine`}
                                          >
                                            <i className={`pe-7s-${editingFinancial ? 'close' : 'edit'}`}></i> {editingFinancial ? t("Cancel") : t("Edit")}
                                          </button>
                                        </Tooltip>
                                      </div>

                                      {editingFinancial ? (
                                        <div>
                                          <Row>
                                            <Col md="12" className="mb-3">
                                              <label className="form-label fw-bold">{t("Bank Name")}</label>
                                              <select
                                                name="bankName"
                                                value={financialInfo.bankName}
                                                onChange={handleFinancialChange}
                                                className="form-select form-select-sm"
                                              >
                                                <option value="">{t("Select Bank")}</option>
                                                {banks && banks.length > 0 ? banks.map((bank) => (
                                                  <option key={bank._id} value={bank.name}>{bank.name}</option>
                                                )) : (
                                                  <option value="" disabled>{t("Loading banks...")}</option>
                                                )}
                                              </select>
                                            </Col>
                                          </Row>
                                          <Row>
                                            <Col md="6" className="mb-3">
                                              <label className="form-label fw-bold">{t("Account Number")}</label>
                                              {!isProtected || isUnlocked ? (
                                              <input
                                                type="text"
                                                name="accountNumber"
                                                value={financialInfo.accountNumber}
                                                onChange={handleFinancialChange}
                                                className="form-control form-control-sm"
                                                placeholder={t("Enter account number")}
                                              />
                                              ) : (
                                                <div className="form-control form-control-sm bg-light">
                                                  <SensitiveText value={financialInfo.accountNumber} />
                                                </div>
                                              )}
                                            </Col>
                                            <Col md="6" className="mb-3">
                                              <label className="form-label fw-bold">{t("Account Holder Name")}</label>
                                              <input
                                                type="text"
                                                name="accountHolderName"
                                                value={financialInfo.accountHolderName}
                                                onChange={handleFinancialChange}
                                                className="form-control form-control-sm"
                                                placeholder={t("Enter account holder name")}
                                              />
                                            </Col>
                                          </Row>
                                          <Row>
                                            <Col md="6" className="mb-3">
                                              <label className="form-label fw-bold">{t("Payment Terms")}</label>
                                              <select
                                                name="paymentTerms"
                                                value={financialInfo.paymentTerms}
                                                onChange={handleFinancialChange}
                                                className="form-select form-select-sm"
                                              >
                                                <option value="">{t("Select Terms")}</option>
                                                <option value="Weekly">{t("Weekly")}</option>
                                                <option value="Bi-Weekly">{t("Bi-Weekly")}</option>
                                                <option value="Monthly">{t("Monthly")}</option>
                                              </select>
                                            </Col>
                                            <Col md="6" className="mb-3">
                                              <label className="form-label fw-bold">{t("Commission Rate")} (%)</label>
                                              <input
                                                type="number"
                                                name="commissionRate"
                                                value={financialInfo.commissionRate}
                                                onChange={handleFinancialChange}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                className="form-control form-control-sm"
                                                placeholder={t(COMMISSION_PLACEHOLDER)}
                                              />
                                            </Col>
                                          </Row>
                                          <div className="d-flex justify-content-end mt-3">
                                            <button onClick={handleSaveFinancial} className="btn btn-success btn-hover-shine">
                                              <i className="pe-7s-check"></i> {t("Save Changes")}
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div>
                                          {merchant.bankInfo ? (
                                            <>
                                              <Row>
                                                <Col md="12" className="mb-3">
                                                  <div className="d-flex flex-column">
                                                    <span className="text-muted small fw-bold">{t("Bank Name")}</span>
                                                    <span className="text-dark">{merchant.bankInfo.bankName || t("Not specified")}</span>
                                                  </div>
                                                </Col>
                                              </Row>
                                              <Row>
                                                <Col md="12" className="mb-3">
                                                  <div className="d-flex flex-column">
                                                    <span className="text-muted small fw-bold">{t("Account Number")}</span>
                                                    <span className="text-dark">
                                                      {merchant.bankInfo.accountNumber
                                                        ? <SensitiveText value={merchant.bankInfo.accountNumber} />
                                                        : t("Not specified")}
                                                    </span>
                                                  </div>
                                                </Col>
                                              </Row>
                                              <Row>
                                                <Col md="12" className="mb-3">
                                                  <div className="d-flex flex-column">
                                                    <span className="text-muted small fw-bold">{t("Account Holder Name")}</span>
                                                    <span className="text-dark">{merchant.bankInfo.accountHolderName || t("Not specified")}</span>
                                                  </div>
                                                </Col>
                                              </Row>
                                              <Row>
                                                <Col md="6" className="mb-3">
                                                  <div className="d-flex flex-column">
                                                    <span className="text-muted small fw-bold">{t("Payment Terms")}</span>
                                                    <span className="text-dark">{merchant.bankInfo.paymentTerms || t("Not specified")}</span>
                                                  </div>
                                                </Col>
                                                <Col md="6" className="mb-3">
                                                  <div className="d-flex flex-column">
                                                    <span className="text-muted small fw-bold">{t("Commission Rate")}</span>
                                                    <span className="text-dark">
                                                      {merchant.hasCustomCommissionRate &&
                                                      merchant.commissionRate != null &&
                                                      merchant.commissionRate !== ''
                                                        ? `${merchant.commissionRate}%`
                                                        : defaultShare != null
                                                          ? `${t("Default")} (${defaultShare}%)`
                                                          : t("Default")}
                                                    </span>
                                                  </div>
                                                </Col>
                                              </Row>
                                            </>
                                          ) : (
                                            <div className="text-muted fst-italic">{t("No bank information provided")}</div>
                                          )}
                                        </div>
                                      )}
                                    </CardBody>
                                  </Card>
                                </Col>
                              </Row>
                            </TabPane>

                            {/* Tab 4: Profile */}
                            <TabPane tab={t("Profile")} key="profile">
                              <Row>
                            <Col md="6" className="mb-4">
                              <Card className="h-100">
                                <CardBody>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0 fw-bold">
                                      <i className="pe-7s-user me-2 text-primary"></i>
                                      {t("Basic Information")}
                                    </h5>
                                    <Tooltip placement="top" title={editingBasic ? t("Cancel Edit") : t("Edit Information")} arrow>
                <button
                  onClick={() => setEditingBasic(!editingBasic)}
                                        className={`btn btn-sm ${editingBasic ? 'btn-secondary' : 'btn-custom-color'} btn-hover-shine`}
                >
                                        <i className={`pe-7s-${editingBasic ? 'close' : 'edit'}`}></i> {editingBasic ? t("Cancel") : t("Edit")}
                </button>
                                    </Tooltip>
              </div>

               {editingBasic ? (
                     <div>
                                      <Row>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Merchant Name")} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="merchantName"
                        value={basicInfo.merchantName}
                        onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter merchant name")}
                        required
                      />
                                        </Col>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Merchant Type")}
                        </label>
                      <select
                        name="merchantType"
                        value={basicInfo.merchantType}
                        onChange={handleBasicInfoChange}
                                            className="form-select form-select-sm"
                                          >
                                            <option value="">{t("Select Type")}</option>
                                            <option value="Pharmacy">{t("Pharmacy")}</option>
                                            <option value="IoT & Lifestyle">{t("IoT & Lifestyle")}</option>
                      </select>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Company Name")}
                     </label>
                    <input
                      type="text"
                      name="companyName"
                      value={basicInfo.companyName}
                      onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter company name")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Registration Number")}
                        </label>
                      {!isProtected || isUnlocked ? (
                      <input
                        type="text"
                        name="registrationNumber"
                        value={basicInfo.registrationNumber}
                        onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter registration number")}
                                          />
                      ) : (
                        <div className="form-control form-control-sm bg-light">
                          <SensitiveText value={basicInfo.registrationNumber} />
                        </div>
                      )}
                                        </Col>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Business Category")}
                        </label>
                      <input
                        type="text"
                        name="businessCategory"
                        value={basicInfo.businessCategory}
                        onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter business category")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Contact Person")}
                     </label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={basicInfo.contactPerson}
                      onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter contact person name")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Contact Email")}
                     </label>
                    <input
                      type="email"
                      name="contactEmail"
                      value={basicInfo.contactEmail}
                      onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter contact email")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Contact Phone")}
                        </label>
                      <input
                        type="tel"
                        name="contactPhone"
                        value={basicInfo.contactPhone}
                        onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter contact phone")}
                                          />
                                        </Col>
                                        <Col md="6" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Alternative Phone")}
                        </label>
                                          <input
                                            type="tel"
                                            name="alternativePhone"
                                            value={basicInfo.alternativePhone}
                                            onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter alternative phone")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-1">
                                          <label className="form-label fw-bold small">
                                            {t("Login Email")}
                     </label>
                                          <input
                                            type="email"
                                            name="loginEmail"
                                            value={basicInfo.loginEmail}
                                            onChange={handleBasicInfoChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter login email")}
                                          />
                                        </Col>
                                      </Row>
                                    
                                    {/* Save Changes Button */}
                                    <div className="d-flex justify-content-end mt-3">
                                      <button
                                        onClick={handleSaveBasicInfo}
                                        className="btn btn-success btn-hover-shine"
                                      >
                                        <i className="pe-7s-check"></i> {t("Save Changes")}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                    <div>
                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Merchant Name")}</span>
                                            <span className="text-dark">
                                              {merchant.merchantName || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Merchant Type")}</span>
                                            <span className="text-dark">
                                              {merchant.merchantType || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Company Name")}</span>
                                            <span className="text-dark">
                                              {merchant.companyName || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Registration Number")}</span>
                                            <span className="text-dark">
                                              {merchant.registrationNumber
                                                ? <SensitiveText value={merchant.registrationNumber} />
                                                : t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Business Category")}</span>
                                            <span className="text-dark">
                                              {merchant.businessCategory || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Contact Person")}</span>
                                            <span className="text-dark">
                                              {merchant.contactPerson || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Contact Email")}</span>
                                            <span className="text-dark">
                                              {merchant.contactEmail || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Contact Phone")}</span>
                                            <span className="text-dark">
                                              {merchant.contactPhone || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                  {merchant.alternativePhone && (
                                        <Row>
                                          <Col md="6" className="mb-3">
                                            <div className="d-flex flex-column">
                                              <span className="text-muted small fw-bold">{t("Alternative Phone")}</span>
                                              <span className="text-dark">
                        {merchant.alternativePhone}
                      </span>
                    </div>
                                          </Col>
                                        </Row>
                                      )}

                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Login Email")}</span>
                                            <span className="text-dark">
                                              {merchant.loginEmail || t("Not specified")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>
                </div>
              )}
                                </CardBody>
                              </Card>
                            </Col>

            {/* Address Section */}
                            <Col md="6" className="mb-4">
                              <Card className="h-100">
                                <CardBody>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0 fw-bold">
                                      <i className="pe-7s-map-marker me-2 text-primary"></i>
                                      {t("Address Information")}
                                    </h5>
                                    <Tooltip placement="top" title={editingAddress ? t("Cancel Edit") : t("Edit Address")} arrow>
                <button
                  onClick={() => setEditingAddress(!editingAddress)}
                                        className={`btn btn-sm ${editingAddress ? 'btn-secondary' : 'btn-custom-color'} btn-hover-shine`}
                >
                                        <i className={`pe-7s-${editingAddress ? 'close' : 'edit'}`}></i> {editingAddress ? t("Cancel") : t("Edit")}
                </button>
                                    </Tooltip>
              </div>

              {editingAddress ? (
                  <div>
                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Address Line 1")}
                    </label>
                                          <input
                                            type="text"
                                            name="line1"
                                            value={addressInfo.line1}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter address line 1")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Address Line 2")}
                    </label>
                                          <input
                                            type="text"
                                            name="line2"
                                            value={addressInfo.line2}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter address line 2")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("City")}
                      </label>
                                          <input
                                            type="text"
                                            name="city"
                                            value={addressInfo.city}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter city")}
                                          />
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("State/Province")}
                      </label>
                                          <input
                                            type="text"
                                            name="state"
                                            value={addressInfo.state}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter state/province")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="6" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Postal Code")}
                      </label>
                                          <input
                                            type="text"
                                            name="postcode"
                                            value={addressInfo.postcode}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter postal code")}
                                          />
                                        </Col>
                                        <Col md="6" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Country")}
                      </label>
                                          <input
                                            type="text"
                                            name="country"
                                            value={addressInfo.country}
                                            onChange={handleAddressChange}
                                            className="form-control form-control-sm"
                                            placeholder={t("Enter country")}
                                          />
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Google Maps Location")}
                    </label>
                    <input
                      type="text"
                      name="googleMapLocation"
                      value={addressInfo.googleMapLocation}
                      onChange={handleAddressChange}
                      placeholder="https://maps.google.com/?q=..."
                                            className="form-control form-control-sm"
                    />
                                        </Col>
                                      </Row>

                                      <div className="d-flex justify-content-end mt-3">
                    <button
                      onClick={handleSaveAddress}
                                          className="btn btn-success btn-hover-shine"
                    >
                                          <i className="pe-7s-check"></i> {t("Save Changes")}
                    </button>
                  </div>
                </div>
              ) : (
                                    <div>
                  {merchant.address ? (
                    <>
                                          <Row>
                                            <Col md="12" className="mb-3">
                                              <div className="d-flex flex-column">
                                                <span className="text-muted small fw-bold">{t("Address")}</span>
                                                <span className="text-dark">
                          {[
                            merchant.address.line1,
                            merchant.address.line2,
                            `${merchant.address.city || ""} ${
                              merchant.address.state || ""
                            } ${merchant.address.postcode || ""}`,
                            merchant.address.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                                            </Col>
                                          </Row>

                      {merchant.address.googleMapLocation && (
                                            <Row>
                                              <Col md="12" className="mb-3">
                                                <div className="d-flex flex-column">
                                                  <span className="text-muted small fw-bold">{t("Google Maps")}</span>
                          <a
                            href={merchant.address.googleMapLocation}
                            target="_blank"
                            rel="noopener noreferrer"
                                                    className="text-primary text-decoration-none"
                          >
                                                    <i className="pe-7s-map-marker me-1"></i>
                                                    {t("View on Google Maps")}
                          </a>
                        </div>
                                              </Col>
                                            </Row>
                      )}
                    </>
                  ) : (
                                        <Row>
                                          <Col md="12" className="mb-3">
                                            <div className="text-muted fst-italic">
                                              {t("No address provided")}
                    </div>
                                          </Col>
                                        </Row>
                  )}
                </div>
              )}
                                </CardBody>
                              </Card>
                            </Col>
                              </Row>
                            </TabPane>

                            {/* Tab 5: Store & Inventory */}
                            <TabPane tab={t("Store & Inventory")} key="store">
                              <StatsPanel loading={statsLoading} stats={merchantStats} t={t}>
                                <Row>
                                  <Col md="12" className="mb-4">
                                    <Card className="border-0 shadow-sm">
                                      <CardBody>
                                        <h6 className="fw-bold mb-3">
                                          <i className="pe-7s-box1 me-2 text-info"></i>
                                          {t("Inventory Statistics")}
                                        </h6>
                                        <Row>
                                          <Col md="3"><DetailRow label={t("Active Items")} value={merchantStats?.inventory?.total_inventory || 0} /></Col>
                                          <Col md="3"><DetailRow label={t("Low on Stock (≤5)")} value={merchantStats?.inventory?.low_on_stock || 0} highlight="text-warning" /></Col>
                                          <Col md="3"><DetailRow label={t("Out of Stock")} value={merchantStats?.inventory?.out_of_stock || 0} highlight="text-danger" /></Col>
                                          <Col md="3"><DetailRow label={t("New This Month")} value={merchantStats?.inventory?.new_items || 0} /></Col>
                                        </Row>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </StatsPanel>
                              <Row>
            {/* Store Settings Section */}
                            <Col md="12" className="mb-4">
                              <Card className="h-100">
                                <CardBody>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0 fw-bold">
                                      <i className="pe-7s-settings me-2 text-primary"></i>
                                      {t("Store Settings")}
                                    </h5>
                                    <Tooltip placement="top" title={editingSettings ? t("Cancel Edit") : t("Edit Settings")} arrow>
                <button
                  onClick={() => setEditingSettings(!editingSettings)}
                                        className={`btn btn-sm ${editingSettings ? 'btn-secondary' : 'btn-custom-color'} btn-hover-shine`}
                >
                                        <i className={`pe-7s-${editingSettings ? 'close' : 'edit'}`}></i> {editingSettings ? t("Cancel") : t("Edit")}
                </button>
                                    </Tooltip>
              </div>

              {editingSettings ? (
                  <div>
                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Store Description")}
                    </label>
                    <textarea
                      name="storeDescription"
                      value={settingsInfo.storeDescription}
                      onChange={handleSettingsChange}
                      rows="4"
                                            className="form-control form-control-sm"
                    ></textarea>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <label className="form-label fw-bold">
                                            {t("Opening Hours")}
                    </label>
                                          <div className="border rounded p-3">
                    {[
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                      "sunday",
                    ].map((day) => (
                      <div
                        key={day}
                                                className="d-flex align-items-center mb-2"
                      >
                                                <span className="w-25 text-capitalize fw-bold small">{t(day)}</span>
                        <input
                          type="time"
                          value={settingsInfo.openingHours[day].open}
                          onChange={(e) =>
                            handleOpeningHoursChange(
                              day,
                              "open",
                              e.target.value
                            )
                          }
                                                  className="form-control form-control-sm me-2"
                                                  style={{ maxWidth: '120px' }}
                        />
                                                <span className="me-2">{t("to")}</span>
                        <input
                          type="time"
                          value={settingsInfo.openingHours[day].close}
                          onChange={(e) =>
                            handleOpeningHoursChange(
                              day,
                              "close",
                              e.target.value
                            )
                          }
                                                  className="form-control form-control-sm"
                                                  style={{ maxWidth: '120px' }}
                        />
                      </div>
                    ))}
                  </div>
                                        </Col>
                                      </Row>

                                      <div className="d-flex justify-content-end mt-3">
                    <button
                      onClick={handleSaveSettings}
                                          className="btn btn-success btn-hover-shine"
                    >
                                          <i className="pe-7s-check"></i> {t("Save Changes")}
                    </button>
                  </div>
                </div>
              ) : (
                                    <div>
                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Store Description")}</span>
                                            <span className="text-dark">
                                              {merchant.storeDescription || t("No description available")}
                    </span>
                  </div>
                                        </Col>
                                      </Row>

                                      <Row>
                                        <Col md="12" className="mb-3">
                                          <div className="d-flex flex-column">
                                            <span className="text-muted small fw-bold">{t("Opening Hours")}</span>
                    {merchant.openingHours ? (
                                              <div className="row mt-2">
                        {Object.entries(merchant.openingHours).map(
                          ([day, hours]) => (
                                                    <div key={day} className="col-6 mb-2">
                                                      <div className="d-flex">
                                                        <span className="text-capitalize fw-bold small me-2" style={{ minWidth: '80px' }}>
                                                          {t(day)}:
                              </span>
                                                        <span className="text-dark small">
                                {hours.open && hours.close
                                  ? `${hours.open} - ${hours.close}`
                                                            : t("Closed")}
                              </span>
                                                      </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                                              <span className="text-muted fst-italic">
                                                {t("No opening hours specified")}
                      </span>
                    )}
                  </div>
                                        </Col>
                                      </Row>
                </div>
              )}
                                </CardBody>
                              </Card>
                            </Col>
                              </Row>
                            </TabPane>

                            {/* Tab 6: Documents */}
                            <TabPane tab={t("Documents")} key="documents">
                              <Row>
            {/* Documents Section */}
                            <Col md="12" className="mb-4">
                              <Card>
                                <CardBody>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0 fw-bold">
                                      <i className="pe-7s-file me-2 text-primary"></i>
                                      {t("Documents")}
                                    </h5>
              </div>

                <div>
                                    {/* Logo Upload */}
                                    <div className="mb-4">
                                      <h6 className="fw-bold mb-3">
                                        <i className="pe-7s-picture me-2 text-success"></i>
                                        {t("Store Logo")}
                                      </h6>
                                      <div className="d-flex align-items-center">
                    {getMerchantLogoUrl(merchant.logo) ? (
                                          <div className="d-flex align-items-center me-4">
                        <img
                          src={getMerchantLogoUrl(merchant.logo)}
                          alt="Store Logo"
                                              className="rounded-circle me-3"
                                              style={{
                                                width: '64px',
                                                height: '64px',
                                                objectFit: 'cover',
                                                border: '3px solid #e0e0e0'
                                              }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/64";
                          }}
                        />
                        <div>
                                              <p className="mb-1 fw-bold text-dark">{t("Current Logo")}</p>
                                              <p className="text-muted small mb-0">
                                                {t("Uploaded on")}: {merchant.logo.uploadDate ? new Date(merchant.logo.uploadDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : t("Unknown date")}
                          </p>
                        </div>
                      </div>
                    ) : (
                                          <div className="text-muted fst-italic me-4">
                                            {t("No logo uploaded")}
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          e.target.value = "";
                          if (!file) return;

                          try {
                            const uploadedPath = await uploadFile(file, "image");
                            const response = await axios.put(
                              `${API_BASE_URL}/MerchantModule/update-settings/${id}`,
                              {
                                logo: uploadedPath,
                              }
                            );

                            if (response.data.success) {
                              setMerchant(response.data.data);
                              Swal.fire({
                                icon: "success",
                                title: t("Logo Updated"),
                                text: t("Store logo has been updated successfully"),
                                confirmButtonColor: "#00BFA6",
                              });
                            }
                          } catch (error) {
                            console.error("Error uploading logo:", error);
                            Swal.fire({
                              icon: "error",
                              title: t("Upload Failed"),
                              text: error.response?.data?.message || t("Failed to upload logo"),
                            });
                          }
                        }}
                                            className="d-none"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                                            className="btn btn-success btn-hover-shine cursor-pointer"
                      >
                                            <i className="pe-7s-upload me-1"></i>
                                            {merchant.logo ? t("Change Logo") : t("Upload Logo")}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Dynamic Documents */}
                {merchantDocs
                  .filter(
                    (doc) =>
                      doc.status === "active" &&
                      (doc.type === "both" ||
                        (doc.type === "pharmacy" &&
                          merchant.businessCategory === "Pharmacy") ||
                        (doc.type === "commerce" &&
                          merchant.businessCategory === "eCommerce"))
                  )
                  .map((doc) => {
                    const docKey = doc.title.toLowerCase().replace(/\s+/g, "");
                    const document = merchant.documents?.find(
                      (d) => d.type === docKey
                    );
                    return (
                      <div key={doc._id} className="mb-4">
                                            <h6 className="fw-bold mb-3">
                                              <i className="pe-7s-file me-2 text-info"></i>
                          {doc.title}
                                            </h6>
                        {document ? (
                                              <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                                                <div className="d-flex align-items-center">
                                                  <div className="me-3">
                                                    <i className="pe-7s-file text-primary fs-3"></i>
                              </div>
                              <div>
                                                    <p className="mb-1 fw-bold text-dark">
                                  {getDisplayFileName(document)}
                                </p>
                                                    <p className="text-muted small mb-0">
                                                      {t("Uploaded on")}: {new Date(document.uploadDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                                                <div className="d-flex gap-2">
                                                  <Tooltip placement="top" title={t("View Document")} arrow>
                              <a
                                href={`${FILE_BASE_URL}/getfiles/merchants/${document.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                                      className="btn btn-outline-primary btn-sm btn-hover-shine"
                                onClick={(e) => {
                                  e.preventDefault();
                                  runSensitive(() => {
                                    window.open(
                                      `${FILE_BASE_URL}/getfiles/merchants/${document.filename}`,
                                      "_blank",
                                      "noopener,noreferrer"
                                    );
                                  });
                                }}
                              >
                                                      <i className="pe-7s-look"></i> {t("View")}
                              </a>
                                                  </Tooltip>
                                                  <Tooltip placement="top" title={t("Replace Document")} arrow>
                              <button
                                onClick={() => {
                                  setDocumentType(docKey);
                                    fileInputRef.current.click();
                                }}
                                                      className="btn btn-outline-warning btn-sm btn-hover-shine"
                              >
                                                      <i className="pe-7s-refresh"></i> {t("Replace")}
                              </button>
                                                  </Tooltip>
                            </div>
                          </div>
                        ) : (
                                              <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                                                <div className="text-muted fst-italic">
                                                  {t("No")} {doc.title.toLowerCase()} {t("uploaded")}
                            </div>
                                                <Tooltip placement="top" title={t("Upload Document")} arrow>
                            <button
                              onClick={() => {
                                setDocumentType(docKey);
                                                      fileInputRef.current.click();
                              }}
                                                    className="btn btn-success btn-sm btn-hover-shine"
                            >
                                                    <i className="pe-7s-upload"></i> {t("Upload")}
                            </button>
                                                </Tooltip>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Document Upload Form */}
                                    <div className="mt-4">
                  <input
  type="file"
  id="document-upload"
                                        ref={fileInputRef}
  onChange={(e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("document", file);
      setUploadingDocument(true);
      axios
        .post(
          `${API_BASE_URL}/MerchantModule/upload-document/${id}/${documentType}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response) => {
          if (response.data.success) {
            setMerchant(response.data.data);
            Swal.fire({
              icon: "success",
                                                    title: t("Document Uploaded"),
                                                    text: t("Document has been uploaded successfully"),
              confirmButtonColor: "#00BFA6",
            });
          }
        })
        .catch((error) => {
          console.error("Error uploading document:", error);
          Swal.fire({
            icon: "error",
                                                  title: t("Upload Failed"),
                                                  text: error.response?.data?.message || t("Failed to upload document"),
          });
        })
        .finally(() => {
          setUploadingDocument(false);
          e.target.value = "";
        });
    }
  }}
                                        className="d-none"
/>
                </div>
              </div>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                            </TabPane>
                          </Tabs>
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MerchantView;
